import { useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { DefaultSelected, SimpleModal } from "src/app/components";
import Pacman from "src/app/components/layout/Pacman";
import { useFindKeywordsVictory, useFrequencyOptions, useWritingTypeOptions } from "src/app/hooks";
import { OrderTransport } from "src/app/transports/order";
import { Frequency, TFrequency } from "src/app/types/CreateOrders";
import { IVictoryKeyword } from "src/app/types/keywords";
import { TWritingType, WritingType } from "src/app/types/orders/order";

const SeoByAiModelContainer = ({
    moneySiteId,
    showModel,
    handleCloseModel,
}: {
    moneySiteId?: number;
    showModel: boolean;
    handleCloseModel(): void;
}) => {
    const { formatMessage } = useIntl();

    const [selectedKeywords, setSelectedKeywords] = useState<Array<IVictoryKeyword>>([]);
    const [currentFrequency, setCurrentFrequency] = useState<TFrequency>(Frequency.TWO_EVERYDAY);
    const [currentWritingType, setCurrentWritingType] = useState<TWritingType>(WritingType.AI_AUTO);
    const [loading, setLoading] = useState(false);

    const { victoryKeywords, loading: findKeywordsVictoryLoading } =
        useFindKeywordsVictory(moneySiteId);

    const { frequencyOptions } = useFrequencyOptions([
        Frequency.TWO_EVERYDAY,
        Frequency.EVERYDAY,
        Frequency.EVERY_2_DAYS,
    ]);

    const { writingTypeOptions } = useWritingTypeOptions([
        WritingType.AI_AUTO,
        WritingType.AI_MANUAL,
    ]);
    const selectedWritingTypeOption = writingTypeOptions.find(
        (option) => option.value === currentWritingType
    );
    const price =
        selectedWritingTypeOption?.value === "AI_AUTO"
            ? Number(process.env.REACT_APP_KEYWORDS_AUTO_AI_PRICE || 0)
            : Number(process.env.REACT_APP_KEYWORDS_MANUAL_AI_PRICE || 0);

    function handleSelectedKeyword(
        victoryKeyword: IVictoryKeyword | "REMOVE_KEYWORDS" | "ALL_KEYWORDS"
    ) {
        if (!victoryKeywords) return;
        if (victoryKeyword === "REMOVE_KEYWORDS") {
            setSelectedKeywords([]);

            return;
        }
        if (victoryKeyword === "ALL_KEYWORDS") {
            setSelectedKeywords(victoryKeywords);

            return;
        }

        setSelectedKeywords((values) => {
            const index = values.findIndex((val) => val.keyword === victoryKeyword.keyword);

            if (index > -1) {
                values.splice(index, 1);
                return [...values];
            } else {
                return [...values, victoryKeyword];
            }
        });
    }

    function onFrequencyChange(frequency: TFrequency) {
        setCurrentFrequency(frequency);
    }

    function onWritingTypeChange(writingType: TWritingType) {
        setCurrentWritingType(writingType);
    }
    useEffect(() => {
        if (victoryKeywords && selectedKeywords.length === 0) {
            setSelectedKeywords(victoryKeywords);
        }
    }, [victoryKeywords]);

    async function onSubmit() {
        if (selectedKeywords.length === 0 || !moneySiteId) return;
        setLoading(true);
        try {
            await OrderTransport.createOrderForVictoryKeywords({
                moneySiteId,
                victoryKeywords: selectedKeywords,
                frequency: currentFrequency,
                writingType: currentWritingType,
            });
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleCloseModel();
        } catch (e) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    }

    return (
        <SimpleModal
            showModal={showModel}
            formId=""
            loading={findKeywordsVictoryLoading || loading}
            buttonConfirmationAction={onSubmit}
            disableConfirmation={selectedKeywords.length === 0}
            entityName={formatMessage({ id: "common.button.seoByAi" })}
            handleClose={handleCloseModel}
            modalProps={{ size: "lg" }}
        >
            <>
                {findKeywordsVictoryLoading || !victoryKeywords ? (
                    <Pacman />
                ) : (
                    <>
                        <div className="netlinkingWrapper">
                            <div className="d-flex justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-5">
                                    <div className="d-flex align-items-center">
                                        <Form.Label>
                                            {formatMessage({ id: "common.field.frequency" })}:
                                        </Form.Label>
                                        <DefaultSelected
                                            isClearable={false}
                                            value={currentFrequency}
                                            onChange={(value: TFrequency) =>
                                                onFrequencyChange(value)
                                            }
                                            options={frequencyOptions}
                                        />
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <Form.Label>
                                            {formatMessage({ id: "common.field.writingType" })}:
                                        </Form.Label>
                                        <DefaultSelected
                                            isClearable={false}
                                            value={currentWritingType}
                                            onChange={(value: TWritingType) =>
                                                onWritingTypeChange(value)
                                            }
                                            options={writingTypeOptions}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="d-flex justify-content-end mt-2 p-2 bg-light rounded">
                                        <div className="selected-keywords-counter">
                                            <strong>
                                                {formatMessage(
                                                    { id: "common.text.totalKeywords" },
                                                    { count: selectedKeywords.length }
                                                )}{" "}
                                                ({selectedKeywords.length}/{victoryKeywords.length})
                                            </strong>
                                            <span className="badge bg-primary ms-2">
                                                {selectedKeywords.length}
                                            </span>
                                            <div className="d-flex flex-column text-end">
                                                <strong>
                                                    {formatMessage({
                                                        id: "common.text.totalprice",
                                                    })}
                                                    :{" "}
                                                    {new Intl.NumberFormat("de-DE", {
                                                        style: "currency",
                                                        currency: "EUR",
                                                    }).format(selectedKeywords.length * price)}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="netlinkingWrapperTable">
                                <Table bordered>
                                    <thead>
                                        <tr
                                            className={"header-no-Selected"}
                                            onClick={() =>
                                                handleSelectedKeyword(
                                                    selectedKeywords.length ===
                                                        victoryKeywords?.length
                                                        ? "REMOVE_KEYWORDS"
                                                        : "ALL_KEYWORDS"
                                                )
                                            }
                                        >
                                            <th>
                                                <Form.Check
                                                    type="switch"
                                                    checked={
                                                        selectedKeywords.length ===
                                                        victoryKeywords?.length
                                                    }
                                                    onChange={() =>
                                                        handleSelectedKeyword(
                                                            selectedKeywords.length ===
                                                                victoryKeywords?.length
                                                                ? "REMOVE_KEYWORDS"
                                                                : "ALL_KEYWORDS"
                                                        )
                                                    }
                                                />
                                            </th>
                                            <th>
                                                {formatMessage({
                                                    id: "common.field.keywords",
                                                })}
                                            </th>
                                            <th>
                                                {formatMessage({
                                                    id: "common.field.volume",
                                                })}
                                            </th>
                                            <th>
                                                {formatMessage({
                                                    id: "common.field.price",
                                                })}
                                            </th>
                                            <th>
                                                {formatMessage({
                                                    id: "common.field.keywordDifficultyIndex",
                                                })}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {victoryKeywords?.map((victoryKeyword, index) => (
                                            <tr
                                                key={index}
                                                onClick={() =>
                                                    handleSelectedKeyword(victoryKeyword)
                                                }
                                                className={
                                                    selectedKeywords.some(
                                                        (el) =>
                                                            el.keyword === victoryKeyword.keyword
                                                    )
                                                        ? "selected"
                                                        : ""
                                                }
                                            >
                                                <td>
                                                    <Form.Check
                                                        type="switch"
                                                        checked={selectedKeywords.some(
                                                            (el) =>
                                                                el.keyword ===
                                                                victoryKeyword.keyword
                                                        )}
                                                        onChange={() => {}}
                                                    />
                                                </td>
                                                <td>{victoryKeyword.keyword}</td>
                                                <td>{victoryKeyword.volume}</td>
                                                <td>
                                                    {new Intl.NumberFormat("de-DE", {
                                                        style: "currency",
                                                        currency: "EUR",
                                                    }).format(price)}
                                                </td>
                                                <td>{victoryKeyword.kd}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </>
                )}
            </>
        </SimpleModal>
    );
};

export default SeoByAiModelContainer;
